<template>
  <div class="overflow-hidden">
    <section id="slide" class="cp-page py-4">
      <!-- Background animated orbs using Stella brand colors -->
      <div class="cp-bg-orb cp-bg-orb--1"></div>
      <div class="cp-bg-orb cp-bg-orb--2"></div>
      
      <!-- Top Transparent Nav -->
      <div class="container-fluid px-3 px-md-4 mb-3 position-relative" style="z-index: 1050;">
        <div class="d-flex align-items-center justify-content-between">
          <router-link class="navbar-brand m-0 p-0" :to="{name: 'HomePage'}">
            <span class="stella-logo-text" style="font-size: 24px; text-decoration: none; cursor: pointer;">✨ stella✦</span>
          </router-link>
          <button class="cp-btn-ghost py-2 px-4" v-if="!isCreatePlatform" @click="loginPage()">Login</button>
        </div>
      </div>
      <loading-modal :is-loading="isLoading"></loading-modal>

      <div class="container position-relative my-auto" style="z-index: 1;">
        <div class="row justify-content-center">
          <div class="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
            <div class="cp-content-card p-4 p-md-5 text-center">
              
              <!-- Stepper Indicator -->
              <div class="wizard-stepper" v-if="!isPrivateCreator">
                <span class="wizard-step-dot active"></span>
                <span class="wizard-step-dot"></span>
              </div>

              <!-- Creator Avatar Hero -->
              <div class="avatar-glow-wrapper">
                <img v-if="!isAvatarMissing"
                     :src="imageURL"
                     class="avatar-glow-img" @error="imageLoadError">
                <img v-else src="@/assets/images/default_user.png" class="avatar-glow-img">
                <div class="avatar-badge" :style="isPrivateCreator ? 'background: #F3DA6B; color: #111;' : ''">
                  <i :class="isPrivateCreator ? 'fa fa-lock' : 'fa fa-check'"></i>
                </div>
              </div>

              <div class="text-white font-weight-600 font-size-15 mb-1">
                {{ inviteeName ? `@${inviteeName}` : '' }}
              </div>

              <!-- Subscriptions Closed Notice for Private Creator -->
              <template v-if="isPrivateCreator">
                <div class="my-4">
                  <div class="badge rounded-pill mb-3 px-3 py-1 font-size-12" style="background: rgba(243, 218, 107, 0.15); color: #F3DA6B; border: 1px solid rgba(243, 218, 107, 0.3);">
                    <i class="fa fa-lock me-1"></i> Subscribers Only
                  </div>
                  <h4 class="text-white font-weight-700 mb-2">Subscriptions Closed</h4>
                  <p class="text-white opacity-75 font-size-14 mb-4">
                    {{ inviteeName ? `@${inviteeName}` : 'This creator' }}'s profile is currently restricted to existing active subscribers and is not accepting new subscriptions.
                  </p>
                  <div class="d-flex align-items-center justify-content-between gap-3 mt-4">
                    <button class="cp-btn-ghost flex-fill py-2" @click="$router.push('/top-creators')">
                      Explore Creators
                    </button>
                    <button class="cp-btn-primary flex-fill py-2" @click="loginPage()">
                      Existing Subscriber? Log In
                    </button>
                  </div>
                </div>
              </template>

              <!-- Standard Public Checkout Flow -->
              <template v-else>
                <template v-if="!isStella">
                  <h3 class="text-white font-weight-700 mb-2">Join My {{ label }}</h3>
                </template>
                <template v-else>
                  <h4 class="text-white font-weight-700 mb-2">Subscribe to get exclusive perks</h4>
                </template>

                <!-- Perks Checklist -->
                <div class="perk-checklist" v-if="isStella">
                  <div class="perk-item">
                    <div class="perk-icon"><i class="fa fa-check"></i></div>
                    <span>Behind the scenes access</span>
                  </div>
                  <div class="perk-item">
                    <div class="perk-icon"><i class="fa fa-check"></i></div>
                    <span>Custom requests & direct interaction</span>
                  </div>
                  <div class="perk-item">
                    <div class="perk-icon"><i class="fa fa-check"></i></div>
                    <span>Exclusive bonus content & media</span>
                  </div>
                  <template v-if="$route.params.urlPrefix === 'tayloralesia'">
                    <div class="perk-item">
                      <div class="perk-icon"><i class="fa fa-check"></i></div>
                      <span>One-on-One messaging & Private Snapchat</span>
                    </div>
                  </template>
                </div>

                <global-message></global-message>

                <!-- Input Forms -->
                <div class="text-left mt-3">
                  <div class="form-group mb-3" v-if="!isStella">
                    <label class="text-white font-size-13 mb-2 font-weight-500">Your {{ label }} Handle</label>
                    <required-field v-if="isRequired('handle')"/>
                    <input type="text" class="cp-input form-control"
                           name="handle" :placeholder="`@username`"
                           v-model.trim="user.handle">
                  </div>

                  <div class="alert alert-snapchat-danger rounded-3 p-2 mb-3" v-if="!isStella">
                    <small class="text-white font-weight-500">
                      <i class="fa fa-info-circle me-1"></i> Please make sure your {{ label }} handle is correct.
                    </small>
                  </div>

                  <div class="form-group mb-3" v-if="!isStella">
                    <label class="text-white font-size-13 mb-2 font-weight-500">Confirm {{ label }} Handle</label>
                    <span class="danger ms-2" v-if="this.isSubmit && !this.$v.repeatHandle.required">Required field</span>
                    <span class="danger ms-2" v-else-if="this.isSubmit && !this.$v.repeatHandle.sameAs">Handle does not match</span>
                    <input type="text" class="cp-input form-control"
                           name="handle" :placeholder="`Re-enter @username`"
                           v-model.trim="repeatHandle">
                  </div>

                  <div class="form-group mb-4" v-if="!isCreatePlatform">
                    <label class="text-white font-size-13 mb-2 font-weight-500">Email Address</label>
                    <required-field v-if="isRequired('email')"/>
                    <span class="danger ms-2" v-if="isValidEmail()">Not a valid email address</span>
                    <input type="email"
                           placeholder="name@example.com"
                           class="cp-input form-control"
                           v-model="user.email">
                  </div>

                  <!-- Terms & Checkout Button -->
                  <div class="mb-2 text-center" style="font-size: 12px; color: rgba(255, 255, 255, 0.55); line-height: 1.5;">
                    By signing up, you agree to our
                    <router-link :to="{name: 'TermsOfService'}" target="_blank" style="color: #70D5C2; text-decoration: underline;">Terms of Service</router-link>
                    and recurring monthly/annual billing.
                  </div>

                  <button type="submit"
                          class="cp-btn-primary w-100 mt-3"
                          @click="beforeCheckout()">
                    Continue to Checkout <i class="fa fa-arrow-right ms-2"></i>
                  </button>

                  <!-- Trust & Security Badges -->
                  <div class="trust-badge-container">
                    <div class="trust-badge-item">
                      <i class="fa fa-shield-alt text-success me-1"></i> 256-Bit SSL Encrypted
                    </div>
                    <div class="trust-badge-item">
                      <i class="fa fa-check-circle text-info me-1"></i> Cancel Anytime
                    </div>
                  </div>
                  
                  <div class="payment-methods-row">
                    <span class="payment-badge">STRIPE SECURED</span>
                    <span class="payment-badge">Apple Pay</span>
                    <span class="payment-badge">Google Pay</span>
                    <span class="payment-badge">Cards</span>
                  </div>
                </div>
              </template>

            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import {mapActions} from 'vuex'
import {email, required, sameAs} from "vuelidate/lib/validators";
import GlobalMessage from "@/components/module/shared/GlobalMessage";
import LoadingModal from "@/components/module/shared/LoadingModal";
import {SERVICE_IDENTIY, SERVICE_SUBSCRIPTION} from "@/properties/micro-service";
import {isInstagramPath, isSnapchatPath, isUserActive} from "@/common/comm-service";
import CreatorImage from "@/components/module/shared/CreatorImage";
import {getLoginBody, storeToken} from "@/common/login-service";
import {AXIOS_LOGIN, geDecodedToken, getTokenExp, isRole, isTokenExpired} from "@/common/http-common";
import {commMsg} from "@/properties/common-messages";
import {ACCESS_TOKEN, PLATFORM_INSTAGRAM, PLATFORM_SNAPCHAT, PLATFORM_STELLA} from "@/properties/close-friend";
import {customerAccountMixin} from "@/mixin/customer-account/customer-account-mixin";
import RequiredField from "@/components/module/shared/RequiredField";
import {
  ROUTE_CREATE_CUSTOMER_INSTAGRAM,
  ROUTE_CREATE_CUSTOMER_PLATFORM,
  ROUTE_CREATE_CUSTOMER_SNAPCHAT,
  ROUTE_CREATE_CUSTOMER_TIKTOK
} from "@/router/module/subscriber/customer-routes";
import titleMixin from "../../../../mixin/title-mixin";
import {isCreatorPrivate} from "@/common/creator-access";

export default {
  name: 'CreateAccount',
  components: {GlobalMessage, LoadingModal, CreatorImage, RequiredField},
  mixins: [customerAccountMixin, titleMixin],
  title: 'Create account',
  data() {
    return {
      inviteeName: '',
      isSubmit: false,
      repeatHandle: null,
      loggedInCreatePlatform: false,
      user: {
        handle: '',
        email: null
      }
    }
  },
  validations() {
    let repeatHandle = {
      required, sameAs: sameAs(function () {
        return this.user.handle
      })
    }
    let user = null;
    if (!this.isCreatePlatform) {
      user = {
        handle: {required},
        email: {required, email}
      };
    } else {
      user = {handle: {required}}
    }
    return {user, repeatHandle};
  },
  created() {


    if (!this.isCreatePlatform) {
      /**
       If the customer has logged in and lands at this page, then take the customer to
       appropriate page. This can happen when the user is trying to subscribe to a new
       creator (influencer)
       */
      let tokenExp = getTokenExp(ACCESS_TOKEN);
      if (tokenExp && !isTokenExpired(ACCESS_TOKEN)) {
        if (!isRole('INFLUENCER')) {
          if (this.$route.params.urlPrefix) {
            if (!this.getUser) {
              let customerId = geDecodedToken(ACCESS_TOKEN).id;
              this.getData(`${SERVICE_IDENTIY}/api/v1/customers/${customerId}`, (customer) => {
                this.setUser(customer);
                this.createPlatformForLoggedInUser();
              });
            } else {
              this.createPlatformForLoggedInUser();
            }
          } else {
            this.$router.replace({name: 'CustomerRouter'});
          }
        }
      }
    }

    this.loadInviteeName();
    this.clearAllStore();
    this.loadAvatarImg();

  },
  computed: {
    isPrivateCreator() {
      const param = this.$route.params.urlPrefix;
      const invitee = this.inviteeName || (this.getInvitee && (this.getInvitee.urlSuffix || this.getInvitee.name));
      return isCreatorPrivate(param) || isCreatorPrivate(invitee);
    },
    isCreatePlatform() {
      return (this.$route.query.createPlatform || this.loggedInCreatePlatform);
    }
  },
  methods: {
    ...mapActions(['clearAllStore', 'setUser', 'setInvitee', 'setPlatform', 'setCreatePlatform']),
    loginPage() {
      let redirectedFrom;
      if (this.isInstagram) {
        redirectedFrom = ROUTE_CREATE_CUSTOMER_INSTAGRAM
      } else if (this.isSnapchat) {
        redirectedFrom = ROUTE_CREATE_CUSTOMER_SNAPCHAT;
      } else if (this.isTikTok) {
        redirectedFrom = ROUTE_CREATE_CUSTOMER_TIKTOK;
      } else {
        redirectedFrom = ROUTE_CREATE_CUSTOMER_PLATFORM;
      }
      this.$router.push({
        name: 'LoginMobile',
        params: {urlPrefix: this.$route.params.urlPrefix},
        query: {redirectedFrom}
      });
    },
    goTo(to) {
      this.$router.push({name: to});
    },
    isRequired(attrib) {
      return this.isSubmit && !this.$v.user[attrib].required;
    },
    isValidEmail() {
      return this.isSubmit && !this.$v.user.email.email;
    },
    beforeCheckout() {
      if (this.isPrivateCreator) {
        return;
      }
      if (this.isCreatePlatform) {
        let customerId = geDecodedToken(ACCESS_TOKEN).id;
        this.getData(`${SERVICE_IDENTIY}/api/v1/customers/${customerId}`, (customer) => {
          this.setUser(customer);
          this.checkout();
        });
      } else {
        this.checkout();
      }
    },
    checkout() {

      this.isSubmit = true;
      if (this.isStella) {
        if (this.isCreatePlatform) {
          this.user.handle = this.getUser.email;
          this.repeatHandle = this.getUser.email;
        } else {
          this.user.handle = this.user.email;
          this.repeatHandle = this.user.email;
        }
      }
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (this.isCreatePlatform) {
        this.setCreatePlatform(true);
        if (!this.getUser) {
          let customerId = geDecodedToken(ACCESS_TOKEN).id;
          this.getData(`${SERVICE_IDENTIY}/api/v1/customers/${customerId}`, (customer) => {
            this.setUser(customer);
            this.createPlatform();
          });
        } else {
          this.createPlatform();
        }
      } else {
        let invitee = this.$route.params.urlPrefix;
        this.postUnsecureData(`identity/api/v1/customers/${this.getInfluencerPlatformId}/${invitee}`, this.user, (data) => {
          this.setUser(data);
          this.login();
        });

      }
    },
    createPlatform() {
      let customerId = geDecodedToken(ACCESS_TOKEN).id;
      let customer = this.getUser;
      let custPlatform = customer.customerPlatforms
        .find(customerPlatform => customerPlatform.platform.name === this.getPlatform);
      let url = `${SERVICE_IDENTIY}/api/v1/customers/${customerId}/customer-platform/`;
      let location = {name: 'CustomerRouter', params: {urlPrefix: this.$route.params.urlPrefix}};
      if (custPlatform) {
        this.patchData(`${url}${custPlatform.id}`, {handle: this.user.handle}, (data) => {
          this.$store.dispatch('setMessage', 'Social handle updated successfully.');
          this.$router.replace(location);
        });
      } else {
        this.postData(url, {platformId: this.getPlatformId, handle: this.user.handle}, (customerPlatform) => {
          this.$store.dispatch('setMessage', 'Social handle linked successfully.');
          customer.customerPlatforms.push(customerPlatform);
          this.setUser(customer);
          this.$router.replace(location);
        });
      }
    },
    createPlatformForLoggedInUser() {
      let customer = this.getUser;
      let custPlatform = customer.customerPlatforms
        .find(customerPlatform => customerPlatform.platform.name === this.getPlatform);
      //platform exists, go ahead to 'CustomerRouter', where we decide what to do
      if (custPlatform) {
        this.$router.replace({name: 'CustomerRouter', params: {urlPrefix: this.$route.params.urlPrefix}});
      } else {
        this.loggedInCreatePlatform = true;
      }
    },

    loadInviteeName() {
      let invitee = this.$route.params.urlPrefix;
      this.getUnSecureData(`${SERVICE_IDENTIY}/api/v1/influencers/urlSuffix/${invitee}`, (data) => {

        if (!isUserActive(data)) {
          this.$router.replace({name: 'DisabledAccount'});
        }
        this.inviteeName = data.name;
        this.setInvitee(data);
        this.setUpPlatform(data.id);

      });
    },
    setUpPlatform(id) {

      let platform;
      if (isSnapchatPath(this.$route.path)) {
        platform = PLATFORM_SNAPCHAT;
      } else if (isInstagramPath(this.$route.path)) {
        platform = PLATFORM_INSTAGRAM
      }
      /**
       * This can happen when this page is accessed directly by the user, instead from coming
       * from platform page
       * */
      if (!this.getPlatform || !this.getInfluencerPlatformId) {
        this.getUnSecureData(`${SERVICE_SUBSCRIPTION}/api/v1/influencers/${id}/platform`, (influencerPlatforms) => {
          let infPlatform = influencerPlatforms.find(influencerPlatform => influencerPlatform.platform.name === platform);
          this.setPlatform(infPlatform);
        });
      }
    },

    login() {
      this.isLoading = true;
      let body = getLoginBody(this.user.email, this.user.handle);
      console.log(this.user);

      AXIOS_LOGIN().post('identity/oauth/token', body)
        .then(response => {
          this.isLoading = false;
          storeToken(response);
          this.$router.push({name: 'Subscription'});
        })
        .catch(error => {
          this.isLoading = false;
          if (error.response) {
            if (error.response.status === 400) {
              // this.error = commMsg['Login.Invalid'];
              this.pushGlobalError(commMsg['Login.Invalid']);
            } else {
              // this.error = error.response.data;
              this.pushGlobalError(error.response.data);
            }
          } else if (error.request) {
            this.pushGlobalError(commMsg['Login.Invalid']);
          } else {
            // Something happened in setting up the request that triggered an Error
            this.pushGlobalError(error.message);
          }
        })
    },

  }
}
;
</script>

<style scoped>
/*header.sticky {
  visibility: hidden;
}*/

.alert-snapchat-danger {
  border: 0;
  border-radius: 0;
  background: #FF7D7D;
}

.custom-control-label::before {
  background: transparent;
  border: 2px solid black;
}

@media (min-width: 576px) and (max-width: 991.98px) {
  .mobile-small-text {
    font-size: 12px;
    padding: 0;
  }
}

@media (min-width: 320px) and (max-width: 575px) {
  .mobile-small-text {
    font-size: calc(2.7vw);
    padding: 0;
  }
}

/* Typography */
.join-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  color: #fff;
}
</style>
